import { useState, useCallback, type ReactNode, type MouseEvent } from "react";

interface Particle {
  id: number;
  x: number;
  y: number;
  angle: number;
  color: string;
}

interface JuicyButtonProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  particleColor?: string;
  as?: "button" | "a";
  href?: string;
  target?: string;
  rel?: string;
}

const COLORS = ["#000000", "#555555", "#888888", "#aaaaaa", "#cccccc"];

let particleId = 0;

export default function JuicyButton({
  children,
  onClick,
  className = "",
  disabled = false,
  particleColor,
  as = "button",
  href,
  target,
  rel,
}: JuicyButtonProps) {
  const [pressed, setPressed] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);

  const spawnParticles = useCallback((e: MouseEvent<HTMLElement>) => {
    if (disabled) return;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    const count = 8;
    const newParticles: Particle[] = Array.from({ length: count }, (_, i) => ({
      id: ++particleId,
      x: cx,
      y: cy,
      angle: (360 / count) * i + Math.random() * 20 - 10,
      color: particleColor ?? COLORS[Math.floor(Math.random() * COLORS.length)],
    }));

    setParticles((prev) => [...prev, ...newParticles]);
    setTimeout(() => {
      setParticles((prev) => prev.filter((p) => !newParticles.find((np) => np.id === p.id)));
    }, 600);
  }, [disabled, particleColor]);

  const handleMouseDown = useCallback(() => {
    if (!disabled) setPressed(true);
  }, [disabled]);

  const handleMouseUp = useCallback(() => {
    setPressed(false);
  }, []);

  const handleClick = useCallback((e: MouseEvent<HTMLElement>) => {
    spawnParticles(e);
    onClick?.();
  }, [spawnParticles, onClick]);

  const commonProps = {
    className: `relative overflow-visible select-none ${className}`,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseUp,
    onClick: handleClick,
    style: {
      transform: pressed ? "scale(0.93) translateY(2px)" : "scale(1) translateY(0px)",
      transition: pressed
        ? "transform 0.08s cubic-bezier(0.34, 1.56, 0.64, 1)"
        : "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
    } as React.CSSProperties,
  };

  const content = (
    <>
      {children}
      {/* Particles */}
      {particles.map((p) => (
        <span
          key={p.id}
          className="pointer-events-none absolute rounded-full"
          style={{
            left: p.x,
            top: p.y,
            width: 6,
            height: 6,
            backgroundColor: p.color,
            transform: `translate(-50%, -50%)`,
            animation: `particle-burst-${Math.floor(p.angle)} 0.55s ease-out forwards`,
          }}
        />
      ))}
      <style>{`
        ${particles.map((p) => `
          @keyframes particle-burst-${Math.floor(p.angle)} {
            0% {
              transform: translate(-50%, -50%) rotate(${p.angle}deg) translateX(0px) scale(1);
              opacity: 1;
            }
            100% {
              transform: translate(-50%, -50%) rotate(${p.angle}deg) translateX(28px) scale(0);
              opacity: 0;
            }
          }
        `).join("")}
      `}</style>
    </>
  );

  if (as === "a") {
    return (
      <a href={href} target={target} rel={rel} {...commonProps}>
        {content}
      </a>
    );
  }

  return (
    <button disabled={disabled} {...commonProps}>
      {content}
    </button>
  );
}
